// uploadEngine — v1 (OB-P1, 14 Oct 2026). The resilient engine behind "Subir mis trabajos" (spec A, moved into Manos de Oro by 00049).
//   · Saved on the phone FIRST (IndexedDB: each shrunk photo + its facts + progress). A reload, crash or app switch resumes where it
//     stopped — he never re-picks. Sessions belong to one signed-in account (owner); another account on the phone never sees them.
//   · Reads ONE photo at a time (EXIF, fingerprint of the original, shrink to 2048 px) and releases it — HEIC on iPhone is the choke.
//   · Uploads in batches of 5, 3 at a time, to signed URLs in his MDO inbox; each photo retries 3 times (2, 8, 20 s) and then is
//     marked failed with Retry. Offline → pauses without spending retries; continues when the signal returns. Holds a wake lock.
//   · Hands each job to MDO with an idempotency key (client_ref): a resume right after a kill can never create the job twice.
//   · A module singleton, so the upload keeps going while he moves around the app; the partner app shows a pill and resumes on start.
//   GPS stays on the phone (only used to split two jobs on the same day); it is never sent and is deleted with the session.
import * as exifr from "exifr";
import type { MdoClient } from "./api";

type Meta = {
  key: string; name: string; sha256: string; isOriginal: boolean; takenAt: string | null;
  make?: string; model?: string; software?: string; lat?: number; lng?: number;
  path: string | null; state: "ready" | "uploaded" | "failed";
};
type Session = { id: string; owner: string; created: number; phase: "reading" | "ready" | "uploading" | "done";
  photos: Meta[]; groups: string[][]; imported: boolean[]; singles: number };
export type UploadPhase = "idle" | "reading" | "ready" | "single" | "uploading" | "offline" | "partial" | "done" | "error";
export type UploadSnapshot = { phase: UploadPhase; read: number; toRead: number; jobs: number; total: number; uploaded: number;
  failed: number; singles: number; batch: number; batches: number; resumedWith: number | null; lastDoneAt: number };

const MAX_SIDE = 2048, MAX_PER_JOB = 12, SAME_PLACE_M = 300, BATCH = 5, PARALLEL = 3, DELAYS = [2000, 8000, 20000];

// ── IndexedDB ──────────────────────────────────────────────────────────────────────────────────────────────────────
let dbp: Promise<IDBDatabase | null> | null = null;
function idb(): Promise<IDBDatabase | null> {
  if (!dbp) dbp = new Promise((ok) => {
    try {
      const r = indexedDB.open("mdo-uploads", 1);
      r.onupgradeneeded = () => { r.result.createObjectStore("sessions", { keyPath: "id" }); r.result.createObjectStore("blobs"); };
      r.onsuccess = () => ok(r.result); r.onerror = () => ok(null);
    } catch { ok(null); }
  });
  return dbp;
}
async function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const d = await idb(); if (!d) return null;
  return new Promise((ok) => { try { const q = fn(d.transaction(store, mode).objectStore(store)); q.onsuccess = () => ok(q.result); q.onerror = () => ok(null); } catch { ok(null); } });
}
const saveSession = (s: Session) => tx("sessions", "readwrite", (st) => st.put(s));
const putBlob = (k: string, b: Blob) => tx("blobs", "readwrite", (st) => st.put(b, k));
const getBlob = (k: string) => tx<Blob>("blobs", "readonly", (st) => st.get(k) as IDBRequest<Blob>);
async function dropSession(s: Session) {
  for (const p of s.photos) await tx("blobs", "readwrite", (st) => st.delete(p.key));
  await tx("sessions", "readwrite", (st) => st.delete(s.id));
}
const allSessions = () => tx<Session[]>("sessions", "readonly", (st) => st.getAll() as IDBRequest<Session[]>);

// ── helpers ────────────────────────────────────────────────────────────────────────────────────────────────────────
async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function shrink(file: Blob): Promise<Blob | null> {
  try {
    let src: CanvasImageSource, w: number, h: number, done: () => void;
    try { const bmp = await createImageBitmap(file); src = bmp; w = bmp.width; h = bmp.height; done = () => bmp.close?.(); }
    catch {   // Safari: some HEIC files open only through <img>
      const url = URL.createObjectURL(file); const img = new Image(); img.src = url;
      await img.decode(); src = img; w = img.naturalWidth; h = img.naturalHeight; done = () => { img.src = ""; URL.revokeObjectURL(url); };
    }
    const k = Math.min(1, MAX_SIDE / Math.max(w, h));
    const c = document.createElement("canvas");
    c.width = Math.round(w * k); c.height = Math.round(h * k);
    c.getContext("2d")!.drawImage(src, 0, 0, c.width, c.height);
    done();
    const out: Blob | null = await new Promise((res) => c.toBlob((b) => res(b), "image/jpeg", 0.85));
    c.width = 0; c.height = 0;   // iOS keeps canvas memory until the size is zeroed
    return out;
  } catch { return null; }
}
function meters(a: Meta, b: Meta): number | null {
  if (a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null;
  const R = 6371000, r = Math.PI / 180, dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
const dayKey = (iso: string | null) => { if (!iso) return "sin-fecha"; const d = new Date(iso); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
/** One job = same calendar day and (when both have GPS) within ~300 m; big days split into chunks of 12. */
export function groupMeta(photos: Meta[]): Meta[][] {
  const sorted = [...photos].sort((a, b) => (a.takenAt ? Date.parse(a.takenAt) : 0) - (b.takenAt ? Date.parse(b.takenAt) : 0));
  const groups: Meta[][] = [];
  for (const p of sorted) {
    const g = groups.find((x) => dayKey(x[0].takenAt) === dayKey(p.takenAt) && x.every((q) => (meters(p, q) ?? 0) <= SAME_PLACE_M));
    if (g) g.push(p); else groups.push([p]);
  }
  return groups.flatMap((g) => Array.from({ length: Math.ceil(g.length / MAX_PER_JOB) }, (_, i) => g.slice(i * MAX_PER_JOB, (i + 1) * MAX_PER_JOB)));
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const online = () => (typeof navigator === "undefined" ? true : navigator.onLine);
const waitOnline = () => online() ? Promise.resolve() : new Promise<void>((r) => window.addEventListener("online", () => r(), { once: true }));

// ── the singleton ──────────────────────────────────────────────────────────────────────────────────────────────────
let session: Session | null = null;
let client: MdoClient | null = null;
let owner = "";
let running = false;
let wake: { release: () => Promise<void> } | null = null;
const listeners = new Set<(s: UploadSnapshot) => void>();
let snap: UploadSnapshot = { phase: "idle", read: 0, toRead: 0, jobs: 0, total: 0, uploaded: 0, failed: 0, singles: 0, batch: 0, batches: 0, resumedWith: null, lastDoneAt: 0 };

function inJobs(s: Session): Meta[] { const keys = new Set(s.groups.flat()); return s.photos.filter((p) => keys.has(p.key)); }
function emit(patch: Partial<UploadSnapshot> = {}) {
  if (session) {
    const ph = inJobs(session);
    const uploaded = ph.filter((p) => p.state === "uploaded").length;
    snap = { ...snap, jobs: session.groups.length, total: ph.length, uploaded, failed: ph.filter((p) => p.state === "failed").length,
             singles: session.singles, batches: Math.max(1, Math.ceil(ph.length / BATCH)), batch: Math.min(Math.ceil(ph.length / BATCH), Math.floor(uploaded / BATCH) + 1) };
  }
  snap = { ...snap, ...patch };
  listeners.forEach((l) => l(snap));
}
export function subscribeUploads(fn: (s: UploadSnapshot) => void): () => void { listeners.add(fn); fn(snap); return () => { listeners.delete(fn); }; }
export function uploadSnapshot(): UploadSnapshot { return snap; }

async function holdWake() {
  try { const n = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } };
        if (n.wakeLock && !wake && document.visibilityState === "visible") wake = await n.wakeLock.request("screen"); } catch { wake = null; }
}
if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => { if (running && document.visibilityState === "visible") { wake = null; holdWake(); } });

/** Read the picked photos one at a time, save each on the phone, then group them into jobs. */
async function ownerOf(ctx: { client: MdoClient; owner?: string }): Promise<string> {
  return ctx.owner || (await ctx.client.whoAmI()) || "signed-out";    // the signed-in account, never a shared default
}
export async function addPhotos(files: File[], ctx: { client: MdoClient; owner?: string }): Promise<void> {
  client = ctx.client; owner = await ownerOf(ctx);
  if (session && session.phase !== "done") await dropSession(session);    // a new pick replaces an unsent one
  session = { id: crypto.randomUUID(), owner, created: Date.now(), phase: "reading", photos: [], groups: [], imported: [], singles: 0 };
  await saveSession(session);
  emit({ phase: "reading", read: 0, toRead: files.length, resumedWith: null });
  for (const [i, file] of files.entries()) {
    const blob = await shrink(file);
    if (blob) {
      let ex: Record<string, unknown> = {};
      try { ex = ((await exifr.parse(file, { pick: ["DateTimeOriginal", "CreateDate", "Make", "Model", "Software", "latitude", "longitude"], gps: true })) ?? {}) as Record<string, unknown>; } catch { /* no EXIF */ }
      const taken = (ex.DateTimeOriginal ?? ex.CreateDate ?? null) as Date | string | null;
      const key = `${session.id}:${i}`;
      await putBlob(key, blob);
      session.photos.push({ key, name: file.name || "foto", sha256: await sha256Hex(await file.arrayBuffer()), isOriginal: !!(ex.DateTimeOriginal && ex.Make),
        takenAt: taken ? new Date(taken).toISOString() : null, make: ex.Make as string | undefined, model: ex.Model as string | undefined,
        software: ex.Software as string | undefined, lat: ex.latitude as number | undefined, lng: ex.longitude as number | undefined, path: null, state: "ready" });
      await saveSession(session);                                          // saved after EVERY photo: a kill here loses nothing read
    }
    emit({ read: i + 1 });
  }
  await finishReading();
}
async function finishReading() {
  if (!session) return;
  const all = groupMeta(session.photos);
  session.groups = all.filter((g) => g.length >= 2).map((g) => g.map((p) => p.key));
  session.singles = all.filter((g) => g.length < 2).length;
  session.imported = session.groups.map(() => false);
  if (!session.groups.length) { const had = session.photos.length; await dropSession(session); session = null; emit({ phase: had ? "single" : "error", jobs: 0, total: 0 }); return; }
  session.phase = "ready"; await saveSession(session); emit({ phase: "ready" });
}

/** Send: upload every photo, then hand each job to MDO. Safe to call again at any point (it continues). */
export async function sendUploads(): Promise<void> {
  if (!session || !client || running) return;
  session.phase = "uploading"; await saveSession(session);
  running = true; await holdWake();
  try {
    emit({ phase: online() ? "uploading" : "offline" });
    for (;;) {
      const todo = inJobs(session).filter((p) => p.state === "ready").slice(0, BATCH);
      if (!todo.length) break;
      if (!online()) { emit({ phase: "offline" }); await waitOnline(); emit({ phase: "uploading" }); }
      let urls: { path: string; signed_url: string }[] = [];
      try { urls = (await withOnline(() => client!.uploads(todo.length))).uploads; }
      catch { for (const p of todo) p.state = "failed"; await saveSession(session); emit(); continue; }
      const queue = todo.map((p, i) => ({ p, u: urls[i] }));
      await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, async () => {
        for (let item = queue.shift(); item; item = queue.shift()) await putOne(item.p, item.u);
      }));
    }
    await importReady();
  } finally {
    running = false;
    try { await wake?.release(); } catch { /* released */ } wake = null;
  }
  const left = inJobs(session!).filter((p) => p.state !== "uploaded").length;
  if (!left && session!.imported.every(Boolean)) {
    const n = inJobs(session!).length; await dropSession(session!); session = null;
    emit({ phase: "done", uploaded: n, total: n, failed: 0, lastDoneAt: Date.now() });
  } else emit({ phase: "partial", lastDoneAt: Date.now() });
}
async function putOne(p: Meta, u: { path: string; signed_url: string }) {
  const blob = await getBlob(p.key);
  if (!blob) { p.state = "failed"; await saveSession(session!); emit(); return; }
  for (let attempt = 0; ; attempt++) {
    if (!online()) { emit({ phase: "offline" }); await waitOnline(); emit({ phase: "uploading" }); }
    let ok = false;
    try { const r = await fetch(u.signed_url, { method: "PUT", headers: { "Content-Type": "image/jpeg", "x-upsert": "true" }, body: blob }); ok = r.ok; } catch { ok = false; }
    if (ok) { p.path = u.path; p.state = "uploaded"; await saveSession(session!); emit({ lastDoneAt: Date.now() }); return; }
    if (!online()) { attempt--; continue; }                       // lost signal: not a real attempt
    if (attempt >= DELAYS.length) { p.state = "failed"; await saveSession(session!); emit(); return; }
    await sleep(DELAYS[attempt]);
  }
}
async function withOnline<T>(fn: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    if (!online()) { emit({ phase: "offline" }); await waitOnline(); emit({ phase: "uploading" }); }
    try { return await fn(); }
    catch (e) { if (!online()) { attempt--; continue; } if (attempt >= DELAYS.length) throw e; await sleep(DELAYS[attempt]); }
  }
}
/** Hand every fully-uploaded job to MDO, each with an idempotency key — a repeat after a crash is ignored by MDO. */
async function importReady() {
  if (!session || !client) return;
  const byKey = new Map(session.photos.map((p) => [p.key, p]));
  const ready: { i: number; group: unknown }[] = [];
  session.groups.forEach((g, i) => {
    if (session!.imported[i]) return;
    const ph = g.map((k) => byKey.get(k)!).filter((p) => p.state === "uploaded");
    if (ph.length !== g.length) return;                           // wait until every photo of the job is up
    ready.push({ i, group: { client_ref: `${session!.id}:${i}`, photos: ph.map((p) => ({
      path: p.path, taken_at: p.takenAt, sha256: p.sha256, is_original: p.isOriginal,
      exif_make: p.make ?? null, exif_model: p.model ?? null, exif_software: p.software ?? null, original_name: p.name,
      // no GPS, ever: location stays on the phone
    })) } });
  });
  if (!ready.length) return;
  try {
    await withOnline(() => client!.importGroups(ready.map((r) => r.group)));
    for (const r of ready) session.imported[r.i] = true;
    await saveSession(session);
  } catch { /* stays not-imported; Retry or the next resume sends it again (safe: client_ref) */ }
}

/** On app start / any screen: pick up an unfinished session for THIS account and continue it. */
export async function resumeUploads(ctx: { client: MdoClient; owner?: string }): Promise<number> {
  const who = await ownerOf(ctx);
  if (session && session.owner !== who && !running) { session = null; emit({ phase: "idle" }); }   // another account signed in
  client = ctx.client; owner = who;
  if (running || session) return 0;
  const mine = ((await allSessions()) ?? []).filter((s) => s.owner === owner).sort((a, b) => b.created - a.created);
  const s = mine[0]; if (!s) return 0;
  session = s;
  if (s.phase === "reading") { await finishReading(); return session ? inJobs(session).length : 0; }   // keep what was read
  if (s.phase === "ready") { emit({ phase: "ready" }); return 0; }                                      // he hadn't tapped send yet
  const left = inJobs(s).filter((p) => p.state !== "uploaded").length + s.imported.filter((x) => !x).length;
  for (const p of s.photos) if (p.state === "failed") p.state = "ready";   // a fresh start retries what failed before the kill
  emit({ resumedWith: inJobs(s).filter((p) => p.state !== "uploaded").length });
  if (left) sendUploads();
  return left;
}
export async function retryFailed(): Promise<void> {
  if (!session) return;
  for (const p of session.photos) if (p.state === "failed") p.state = "ready";
  await saveSession(session); emit(); await sendUploads();
}
export async function discardUploads(): Promise<void> { if (session && !running) { await dropSession(session); session = null; emit({ phase: "idle" }); } }
